import { useState } from "react";
import { changedKeys, formatOriginal, type ClearanceCell, type DiffRow } from "@/lib/clearanceMerge";

// Confirmation step for "Re-extract from spec sheet". Nothing has been written
// when this opens; Cancel (or the backdrop) discards the extraction entirely.

function Cell({ cell }: { cell: ClearanceCell }) {
  if (cell.mm === null) return <span className="text-gray-600">—</span>;
  return (
    <span className="text-gray-200">
      {cell.mm} mm
      {cell.original && (
        <span className="ml-2 text-xs text-teal-400" title="Value and unit as printed on the spec sheet">
          printed: {formatOriginal(cell.original)}
        </span>
      )}
    </span>
  );
}

export default function ClearanceReextractDialog({
  rows,
  hasExtractedClearances,
  hasStandards,
  partialWarning,
  saving,
  onConfirm,
  onCancel,
}: {
  rows: DiffRow[];
  /** false when the extraction returned no clearances object at all */
  hasExtractedClearances: boolean;
  /** true when the product has per-standard sets (they are never modified) */
  hasStandards: boolean;
  partialWarning?: string | null;
  saving: boolean;
  onConfirm: (acceptedKeys: string[], refreshSpecs: boolean) => void;
  onCancel: () => void;
}) {
  const changed = changedKeys(rows);
  const [accepted, setAccepted] = useState<Set<string>>(() => new Set(changed));
  const [refreshSpecs, setRefreshSpecs] = useState(true);

  const visible = rows.filter((r) => r.kind !== "unchanged");
  const unchangedCount = rows.length - visible.length;

  function toggle(key: string) {
    setAccepted((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const nothingToApply = accepted.size === 0 && !refreshSpecs;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={saving ? undefined : onCancel} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Review re-extracted specs"
        className="relative rounded-2xl border border-gray-800 bg-gray-900 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto space-y-4 shadow-xl"
      >
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-white">Review re-extracted clearances</h2>
          <p className="text-sm text-gray-400">
            Nothing has been saved yet. Only the fields below are updated — everything else on the clearances
            record{hasStandards ? ", including the per-standard sets (CSA B365 / NFPA 211) and their printed values," : ""} is kept as it is.
          </p>
          {partialWarning && <p className="text-xs text-amber-400">{partialWarning}</p>}
        </div>

        {!hasExtractedClearances ? (
          <p className="text-sm text-gray-500">The extraction returned no clearance data. Current clearances are kept.</p>
        ) : visible.length === 0 ? (
          <p className="text-sm text-gray-500">
            The extracted clearances match the current values — nothing to change
            {unchangedCount > 0 ? ` (${unchangedCount} field${unchangedCount === 1 ? "" : "s"} identical)` : ""}.
          </p>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {accepted.size} of {visible.length} selected
              </span>
              <div className="flex gap-3 text-xs">
                <button
                  type="button"
                  onClick={() => setAccepted(new Set(changed))}
                  disabled={saving}
                  className="text-teal-400 hover:text-teal-300 disabled:opacity-40"
                >
                  Accept all
                </button>
                <button
                  type="button"
                  onClick={() => setAccepted(new Set())}
                  disabled={saving}
                  className="text-gray-400 hover:text-gray-300 disabled:opacity-40"
                >
                  Select none
                </button>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="pb-2 w-8" />
                  <th className="pb-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Field</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Current</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Extracted</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <tr key={row.key} className="border-b border-gray-800/50">
                    <td className="py-2.5">
                      <input
                        type="checkbox"
                        aria-label={`Accept ${row.label}`}
                        checked={accepted.has(row.key)}
                        onChange={() => toggle(row.key)}
                        disabled={saving}
                        className="h-4 w-4 accent-teal-500"
                      />
                    </td>
                    <td className="py-2.5 text-gray-400">
                      {row.label}
                      {row.kind === "new" && <span className="ml-2 text-xs text-green-500">new</span>}
                    </td>
                    <td className="py-2.5"><Cell cell={row.current} /></td>
                    <td className="py-2.5"><Cell cell={row.extracted} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {unchangedCount > 0 && (
              <p className="text-xs text-gray-600">
                {unchangedCount} other field{unchangedCount === 1 ? "" : "s"} identical — not shown.
              </p>
            )}
          </div>
        )}

        <label className="flex items-center gap-2 text-sm text-gray-400">
          <input
            type="checkbox"
            checked={refreshSpecs}
            onChange={(e) => setRefreshSpecs(e.target.checked)}
            disabled={saving}
            className="h-4 w-4 accent-teal-500"
          />
          Also refresh consumer &amp; installer specs from this extraction
        </label>

        <div className="flex gap-3 justify-end pt-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded-lg border border-gray-700 px-4 py-2 text-sm text-gray-400 hover:bg-gray-800 disabled:opacity-40 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(Array.from(accepted), refreshSpecs)}
            disabled={saving || nothingToApply}
            className="rounded-lg bg-teal-700 px-4 py-2 text-sm font-medium text-white hover:bg-teal-600 disabled:opacity-40 transition"
          >
            {saving ? "Saving…" : accepted.size > 0 && accepted.size === changed.length ? "Accept all & save" : "Apply selected"}
          </button>
        </div>
      </div>
    </div>
  );
}
